import { useEffect, useState } from "react";
import { FaCartArrowDown, FaHeart } from "react-icons/fa6";
import { IoChevronDown } from "react-icons/io5";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import FeaturedProducts from "./navbar/FeaturedProducts";

export default function MegaMenuNavbar() {
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // State for mobile menu
  const [expandedCategory, setExpandedCategory] = useState(null); // State for expanded category in mobile view
  const navigate = useNavigate();

  const categories = useSelector((state) => state.maincategory.categories);
  const carts = useSelector((state) => state.cart.carts || []);
  const wishlists = useSelector((state) => state.wishlist.wishlist || []);

  const formattedCategories = categories
    ?.filter((cat) => cat?.parent === null)
    ?.map((fCat) => ({
      ...fCat,
      subCategories: categories?.filter((cat) => cat?.parent?.id === fCat?.id),
    }));

  const handleMouseEnter = (categoryId) => {
    setActiveCategory(categoryId);
  };

  const handleMouseLeave = () => {
    setActiveCategory(null);
  };

  const handleMouseEnterSubCategory = (categoryId) => {
    setActiveSubCategory(categoryId);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setExpandedCategory(null); // Reset expanded category when toggling mobile menu
  };

  const handleCategoryClick = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null); // Collapse if already expanded
    } else {
      setExpandedCategory(categoryId); // Expand the clicked category
    }
  };

  const handleLinkClick = (url) => {
    navigate(url); // Navigate to the link
    setIsMobileMenuOpen(false); // Close mobile menu
    setExpandedCategory(null); // Reset expanded category
  };

  useEffect(() => {
    setActiveSubCategory(
      formattedCategories?.find((cat) => cat.id === activeCategory)
        ?.subCategories?.[0]?.id
    );
  }, [activeCategory]);

  const totalPrice = carts?.reduce((total, item) => {
    // Determine the price based on whether `product` or `variation` exists
    const price = item.product
      ? Number(item.product.selling_price) // Use product price if product exists
      : item.variation
      ? Number(item.variation.selling_price) // Use variation price if variation exists
      : 0; // Default to 0 if neither exists

    // Ensure quantity is a valid number, default to 1 if missing or invalid
    const quantity = item.quantity ? Number(item.quantity) : 1;

    // Log for debugging

    // Add to the total
    return total + price * quantity;
  }, 0);

  return (
    <div className="relative">
      {/* Main Navbar - Hidden on mobile, visible on desktop */}
      <nav className="hidden text-white bg-rose-700 lg:block">
        <div className="container px-4 mx-auto">
          <div className="flex items-center justify-between h-16">
            {/* Logo - can be added here */}
            <div className="hidden md:flex">
              {/* Desktop Navigation */}
              <ul className="flex">
                {formattedCategories?.map((category) => (
                  <li
                    key={category?.id}
                    className="relative group"
                    onMouseEnter={() => handleMouseEnter(category?.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Link
                      to={`/${category?.slug}`}
                      className="flex items-center px-3 py-2 text-sm font-medium transition-colors rounded-md hover:bg-rose-800 text-nowrap"
                    >
                      {category?.name}
                      <IoChevronDown className="w-4 h-4 ml-1" />
                    </Link>
                    {/* Add an invisible bridge to maintain hover */}
                    {activeCategory === category?.id && (
                      <div className="absolute left-0 w-full h-2 -bottom-2" />
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Cart */}
            <div className="flex items-center gap-5">
              <Link
                to="/wishlist"
                className="relative flex items-center text-white"
              >
                <FaHeart className="w-6 h-6" />
                <span className="ml-1 text-sm absolute font-medium z-20 top-0 -translate-y-[50%] right-0 translate-x-[50%] bg-black h-5 w-5 rounded-full flex justify-center items-center">
                  {wishlists?.length}
                </span>
              </Link>

              <Link to="/cart" className="flex items-center text-white">
                <FaCartArrowDown className="w-6 h-6" />
                <span className="ml-1 text-sm font-medium">
                  Rs. {totalPrice || 0.0}
                </span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mega Menu Dropdown - Hidden on mobile, visible on desktop */}
      {activeCategory && (
        <div
          className="absolute left-0 z-50 w-full -mt-2 bg-white border-t shadow-lg border-rose-800"
          onMouseEnter={() => handleMouseEnter(activeCategory)}
          onMouseLeave={handleMouseLeave}
        >
          <div className="container px-4 py-6 mx-auto">
            <div className="flex flex-col w-full md:flex-row">
              {/* Second Level Categories */}
              <div className="flex-1 pr-8 border-r">
                <h3 className="mb-4 text-lg font-bold text-rose-700">
                  {categories.find((cat) => cat.id === activeCategory)?.name}
                </h3>
                <ul className="space-y-3">
                  {formattedCategories
                    ?.find((cat) => cat.id === activeCategory)
                    ?.subCategories?.map((subCategory) => (
                      <li key={subCategory.id}>
                        <Link
                          onMouseEnter={() =>
                            handleMouseEnterSubCategory(subCategory?.id)
                          }
                          to={`/${
                            formattedCategories.find(
                              (cat) => cat?.id === activeCategory
                            )?.slug
                          }/${subCategory?.slug}`}
                          className="text-gray-700 transition-colors hover:text-rose-700 "
                        >
                          {subCategory?.id === activeSubCategory ? (
                            <span className="text-red-700">
                              {subCategory?.name}
                            </span>
                          ) : (
                            <span>{subCategory?.name}</span>
                          )}
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>

              <div className="flex-[6] mt-4 md:mt-0">
                <FeaturedProducts
                  formattedCategories={formattedCategories}
                  activeCategory={activeCategory}
                  activeSubCategory={activeSubCategory}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
